import classNames from "classnames";
import React from "react";
import { TransitionGroup } from "react-transition-group";

import { gGateway } from "@utils/gateway";

import { asyncTimeout } from "@use/async-timeout";
import { memoize } from "@use/decorators";
import { preloadImage } from "@use/preload-image";

import { IframeModel, IframeTargets } from "@courses-pm2/api";
import { getIcon } from "@courses-pm2/icons";
import { Ribbon } from "@courses-pm2/ribbon-util-component";

import { colors, easings } from "@courses-pm2/iframe-component/src/ts/config";
import { isAndroid } from "@courses-pm2/iframe-component/src/ts/utils/isAndroid";
import { isIOS } from "@courses-pm2/iframe-component/src/ts/utils/isIOS";

import { ContentFade } from "@courses-pm2/iframe-component/src/ts/components/transitions/ContentFade";

export type IframeComponentProps = {
  /**
   * The model contains all of the content to render within each instance of
   * the component.
   */
  model: IframeModel;

  /**
   * Specifies the id of the user that's accessing the page (we're going to
   * need this piece of information in order to properly add authentication
   * to certain urls).
   */
  userId?: string;
  institutionId?: string;

  /**
   * If this is specified, then the iframe is currently being displayed inside
   * a lightbox.
   */
  inLightbox?: boolean;

  /**
   * Callback to be called once the component should be opened in a lightbox
   */
  openLightbox?(args: { model: IframeModel; node: HTMLDivElement }): void;
};

type IframeComponentState = {
  /**
   * Specifies the timestamp for when the iframe was activated (if it has been
   * activated yet).e DOM itself rather than a preview image).
   */
  activatedAt?: number;

  /**
   * Specifies if the iframe has been loaded and is ready to be faded into
   * view.
   */
  isReady?: boolean;

  /**
   * Contains the absolute url to the preview image that should be displayed
   * for the iframe before activation; this will only be filled out, once
   * the image has actually been preloaded and is ready for rendering!
   */
  previewImageUrl?: string;

  /**
   * Specifies if an error occurred while trying to load the preview image, in
   * which case we're going to fall back to a text based rendering.
   */
  previewImageError?: boolean;
};

/**
 * Root of the render tree for the standard iframe-component used to render
 * iframe elements hosted within PM2.
 */
export class IframeComponent extends React.PureComponent<
  IframeComponentProps,
  IframeComponentState
> {
  public state: IframeComponentState = {};

  /**
   * Will contain a reference to the node that's wrapping the component.
   */
  protected readonly wrapperRef: React.RefObject<HTMLDivElement | null> =
    React.createRef();

  /**
   * We're going to have to run the url through Gateway v3 secure forward in
   * order to ensure that all requests are properly authenticated. To prevent
   * constantly reloading the iframe, we cache an authenticated url here.
   */
  protected forwardedUrl: string | undefined;

  /**
   * Padding bottom of our component - this is utilized for doing proportional scaling
   */
  protected paddingBottom: number = 0;

  /**
   * OnMount
   */
  public componentDidMount(): void {
    const { model, inLightbox } = this.props;

    // Determine if we should activate the iframe right away depending on
    // the current
    const activated =
      inLightbox || (model.autoActivate && this.allowInPageEmbed());

    // If the iframe isn't going to be instantly activated, then begin
    // preloading a preview image for it instantly
    if (!activated) {
      this.preloadPreviewImage().catch(() => {
        /** Errors are handled within the method */
      });
    }

    // Apply state so the iframe will activate if needed
    if (activated) {
      this.setState({ activatedAt: Date.now() });
    }
  }

  /**
   * Primary render method.
   */
  // tslint:disable-next-line cyclomatic-complexity
  public render(): React.JSX.Element {
    const className = classNames("iframe", {
      "iframe--inLightbox": this.props.inLightbox,
    });

    return (
      <div ref={this.wrapperRef} className={className}>
        {this.renderTop()}
        {this.renderContent()}
        {this.renderBottom()}

        <style jsx>{`
          .iframe {
            /** Add margin between elements */
            margin-bottom: 30px;

            /** Add a very light drop shadow around the iframe */
            box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll render the content specific for the iframe
   */
  protected renderContent(): React.JSX.Element {
    const { activatedAt, previewImageUrl, previewImageError, isReady } =
      this.state;

    // Determine what to actually display based on current state
    const displayPreview = (previewImageUrl || previewImageError) && !isReady;
    const displayPreloader = !displayPreview && !isReady;
    const displayIframe = activatedAt !== undefined;

    // Determine styling data
    const aspectRatio = this.getAspectRatio();
    const maxWidth = this.getMaxWidth();

    return (
      <div
        className="iframe__aspectRatio1"
        style={{
          maxWidth: maxWidth ? `${maxWidth}px` : undefined,
        }}
      >
        <div
          className="iframe__aspectRatio2"
          style={{
            paddingBottom: `${aspectRatio * 100}%`,
          }}
        >
          <TransitionGroup component="div" className="iframe__wrapper">
            {displayPreloader && this.renderPreloader()}
            {displayPreview && this.renderPreview()}
            {displayIframe && this.renderIframe()}
          </TransitionGroup>
        </div>

        <style jsx>{`
          .iframe__aspectRatio1 {
            /** Auto center the container horizontally */
            margin: 0 auto;
          }

          .iframe__aspectRatio2 {
            /**
                         * Allow position: absolute to place the iframe within
                         * this container.
                         */
            position: relative;
          }

          :global(.iframe__wrapper) {
            /** Force iframe to fill up the aspect ratio wrapper */
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Rendered that'll display the preloader while iframe or preview image is
   * being fetched.
   */
  protected renderPreloader(): React.JSX.Element {
    return (
      <ContentFade key="preloader">
        {(ref) => (
          <>
            <div
              ref={ref}
              className="preloader"
              onClick={this.onPreviewClick}
              role="button"
            >
              <i className="icon">
                {getIcon("gyldendal", { fillColor: "rgba(0, 0, 0, 0.25)" })}
              </i>
            </div>

            <style jsx>{`
              .preloader {
                /** Fill entire container */
                display: block;
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;

                /** Indicate that preview is clickable */
                cursor: pointer;

                /* Set a default preloading background color */
                background: rgba(0, 0, 0, 0.1);
              }

              .icon {
                /** Center preload icon within container */
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);

                /** Setup dimensions of icon */
                width: 40px;
                height: 60px;
              }
            `}</style>
          </>
        )}
      </ContentFade>
    );
  }

  /**
   * Renderer that'll render the preview displayed before the actual iframe
   * is loaded.
   */
  protected renderPreview(): React.ReactNode {
    const { previewImageUrl } = this.state;

    return (
      <ContentFade key="preview">
        {(ref) => (
          <>
            <div
              ref={ref}
              className="preview"
              onClick={this.onPreviewClick}
              role="button"
            >
              {previewImageUrl
                ? this.renderPreviewImage()
                : this.renderPreviewText()}
            </div>

            <style jsx>{`
              .preview {
                /** Fill entire container */
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                height: 100%;
                overflow: hidden;

                /** Render background color */
                background-color: rgba(0, 0, 0, 0.1);

                /** Indicate that preview is clickable */
                cursor: pointer;
              }
            `}</style>
          </>
        )}
      </ContentFade>
    );
  }

  /**
   * Renderer that'll render the fallback preview text which will be displayed
   * if we failed to generate a preview image for the given iframe
   */
  protected renderPreviewText(): React.JSX.Element {
    return (
      <div className="preview__text">
        {this.props.model.title || (
          <i className="icon">
            {getIcon("gyldendal", { fillColor: "rgba(0, 0, 0, 0.25)" })}
          </i>
        )}

        <style jsx>{`
          .preview__text {
            /** Fill the container */
            box-sizing: border-box;
            height: 100%;
            width: 100%;

            /** Hide overflowing text */
            padding: 15px;
            overflow: hidden;

            /** Center text inside */
            display: flex;
            align-items: center;
            justify-content: center;

            /** Apply text styling */
            font-size: 18px;
            color: rgba(0, 0, 0, 0.45);

            /** Apply hover transition */
            transition: color 0.1s linear;

            &:hover {
              color: rgba(0, 0, 0, 0.6);
            }
          }

          .icon {
            /** Center preload icon within container */
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);

            /** Setup dimensions of icon */
            width: 40px;
            height: 60px;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll render the preview image before the actual iframe is
   * loaded, if any could be fetched
   */
  protected renderPreviewImage(): React.JSX.Element {
    const { needsAutoGeneratedPreviewImage } = this.props.model;

    return (
      <div className="preview__img">
        <img src={this.state.previewImageUrl} role="presentation" alt="" />

        {needsAutoGeneratedPreviewImage && (
          <div className="preview__imgOverlay">
            <i className="preview__imgOverlay__icon">
              {getIcon("clickable", { fillColor: colors.black })}
            </i>
          </div>
        )}

        <style jsx>{`
          .preview__img {
            /** Fill container */
            width: 100%;
            height: 100%;
          }

          img {
            /** Fill container */
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .preview__imgOverlay {
            /** Center the overlay within the preview image */
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) scale(1);

            /** Set up sizing of the icon */
            box-sizing: border-box;
            width: 120px;
            height: 120px;

            /** Apply box styling */
            background: rgba(255, 255, 255, 0.8);
            border: 4px solid ${colors.black};
            border-radius: 50%;

            /** Apply hover transition */
            transition: background-color 0.2s linear,
              transform 0.3s ${easings.inOutQuad};
          }

          .preview__img:hover .preview__imgOverlay {
            /** Apply hover transition */
            background: rgba(255, 255, 255, 0.9);
            transform: translate(-50%, -50%) scale(1.1);
          }

          .preview__imgOverlay__icon {
            /** Set up sizing of the icon wrapper */
            position: absolute;
            top: 20px;
            bottom: 20px;
            left: 20px;
            right: 20px;
          }

          /** Responsive adjustments to the size of the icon */
          @media screen and (max-width: 1024px) {
            .preview__imgOverlay {
              width: 90px;
              height: 90px;
              border-width: 3px;
            }

            .preview__imgOverlay__icon {
              top: 15px;
              bottom: 15px;
              left: 15px;
              right: 15px;
            }
          }

          @media screen and (max-width: 767px) {
            .preview__imgOverlay {
              width: 60px;
              height: 60px;
              border-width: 2px;
            }

            .preview__imgOverlay__icon {
              top: 10px;
              bottom: 10px;
              left: 10px;
              right: 10px;
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll render the iframe of the component
   */
  protected renderIframe(): React.ReactNode {
    const { isReady } = this.state;

    // Determine if the throbber should be displayed
    const showThrobber = !isReady;

    const className = classNames({
      "iframe--isReady": isReady,
    });

    // tslint:disable react-iframe-missing-sandbox
    return (
      <ContentFade key="iframe">
        {(ref) => (
          <>
            <ContentFade in={showThrobber}>
              {(ref) => <span ref={ref} className="iframe__throbber" />}
            </ContentFade>

            <iframe
              ref={ref}
              className={className}
              scrolling="yes"
              src={this.getForwardedUrl()}
              onLoad={this.onIframeLoad}
            />

            <style jsx>{`
              .iframe__throbber {
                /** Position in the top-right corner */
                position: absolute !important;
                top: 0 !important;
                left: auto !important;
                right: 0 !important;
                z-index: 1 !important;

                /** Add white overlay below the spinner */
                width: 400px;
                height: 400px;
                background: radial-gradient(
                  ellipse farthest-side at top right,
                  rgba(255, 255, 255, 1) 0%,
                  rgba(255, 255, 0, 0) 100%
                );

                &::before {
                  content: "";

                  /** Position the spinner within the overlay */
                  display: "block";
                  position: absolute;
                  top: 30px;
                  right: 30px;

                  /** Set up sizing */
                  box-sizing: border-box;
                  width: 32px !important;
                  height: 32px !important;

                  /** Apply styling */
                  border: 2px solid rgba(0, 0, 0, 0.2);
                  border-top-color: rgba(0, 0, 0, 0.6);
                  border-radius: 50%;

                  /** Spin infinitely */
                  animation: spin 0.9s ${easings.inOutQuad} infinite;
                }
              }

              iframe {
                /** Let iframe fill entire container */
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;

                /** Override defaults */
                border: 0;

                /**
                         * Handle fade-in once the content of the iframe has
                         * been loaded.
                         */
                opacity: 0;
                transition: opacity 275ms linear 125ms;

                &.iframe--isReady {
                  opacity: 1;
                }
              }

              @keyframes spin {
                from {
                  transform: rotate(0deg);
                }
                to {
                  transform: rotate(360deg);
                }
              }
            `}</style>
          </>
        )}
      </ContentFade>
    );
    // tslint:enable react-iframe-missing-sandbox
  }

  /**
   * Renderer that'll inject the bar above the image (if one should be
   * visible).
   */
  protected renderTop(): React.JSX.Element | undefined {
    const { model } = this.props;
    const showCaption =
      (model.title || model.description || model.caption) &&
      model.displayCaptionAbove;

    // Bail out if topbar shouldn't be displayed (ie. if there is no
    // ribbon or no caption available)
    if (!model.ribbon && !showCaption) {
      return undefined;
    }

    const className = classNames("iframe__top", {
      "iframe__top--withRibbon": !!model.ribbon,
    });

    return (
      <div className={className}>
        {this.renderRibbon()}
        {showCaption && this.renderCaption()}

        <style jsx>{`
          .iframe__top {
            /** Overlap the actual iframe */
            position: relative;
            z-index: 2;

            /**
                         * Force container to fit at least a single line of text
                         * within itself
                         */
            min-height: 1.3em;

            /** Apply box styling */
            background: #fff;
            padding: calc(15px - 0.15em) 15px calc(13px - 0.15em);

            /** Apply a solid border above the iframe */
            border-bottom: 2px solid rgba(0, 0, 0, 0.1);

            /**
                         * Render shadow on top of the iframe using pseudo after
                         * element and css gradient.
                         */
            &::after {
              /** Flow right under the topbar */
              position: absolute;
              top: calc(100% + 2px);
              left: 0;
              right: 0;

              /** Apply dropshadow */
              height: 5px;
              background: linear-gradient(
                to bottom,
                rgba(0, 0, 0, 0.05),
                rgba(0, 0, 0, 0)
              );

              /** Force rendering of pseudo element */
              content: "";
            }
          }

          .iframe__top--withRibbon {
            /**
                         * Ribbons will overlap part of the left side of the bar
                         * so we need to adjust padding accordingly!
                         */
            padding-left: 60px;
          }
        `}</style>
      </div>
    );
  }

  /**
   * Renderer that'll inject the bar below the iframe (if one should be
   * visible).
   */
  protected renderBottom(): React.JSX.Element | undefined {
    const { model } = this.props;
    const hasCaption = model.title || model.description || model.caption;

    // Bail out if caption shouldn't be displayed below the iframe
    if (!hasCaption || !model.displayCaptionBelow) {
      return undefined;
    }

    // ... Otherwise render the caption here!
    return (
      <div className="iframe__bottom">
        {this.renderCaption()}

        <style jsx>{`
          .iframe__bottom {
            /** Allow position: absolute inside */
            position: relative;

            /** Apply box styling */
            padding: calc(15px - 0.15em) 15px;

            /** Apply a solid border below the iframe */
            border-top: 2px solid rgba(0, 0, 0, 0.1);

            /**
                         * Render shadow on top of the iframe using pseudo after
                         * element and css gradient.
                         */
            &::after {
              /** Flow right above the bottom */
              position: absolute;
              top: -7px;
              left: 0;
              right: 0;

              /** Apply dropshadow */
              height: 5px;
              background: linear-gradient(
                to top,
                rgba(0, 0, 0, 0.05),
                rgba(0, 0, 0, 0)
              );

              /** Force rendering of pseudo element */
              content: "";
            }
          }
        `}</style>
      </div>
    );
  }

  /**
   * Helper that'll render the <Ribbon /> utility to inject the icon on the
   * top-left side of the component.
   */
  protected renderRibbon(): React.JSX.Element | undefined {
    if (!this.props.model.ribbon) {
      return undefined;
    }

    return <Ribbon iconLetter={this.props.model.ribbon} />;
  }

  /**
   * Renderer that'll inject the caption associated with the image, either in
   * the top or bottom section.
   */
  protected renderCaption(): React.JSX.Element {
    const { model } = this.props;

    // tslint:disable react-no-dangerous-html
    return (
      <React.Fragment>
        {model.title && (
          <span className="captionElement title">{model.renderTitle()}</span>
        )}
        {model.description && (
          <span className="captionElement description">
            {model.renderDescription()}
          </span>
        )}
        {model.caption && (
          <span className="captionElement caption">
            {model.renderCaption()}
          </span>
        )}

        <style jsx>{`
          .captionElement {
            /** Apply font styling */
            line-height: 1.3em;

            /** Apply custom styling to different elements */
            &.title {
              font-weight: bold;
            }

            &.description,
            &.caption {
              font-style: italic;
            }

            /**
                         * Ensure elements are visually separated by inserting a
                         * space in between each of them.
                         */
            &::after {
              content: " ";
            }
          }
        `}</style>
      </React.Fragment>
    );
  }

  /**
   * Listener that's triggered when the preview is being clicked, determining
   * if we should open the iframe in a lightbox or in a new window all
   * together.
   */
  protected onPreviewClick = () => {
    const { model, openLightbox } = this.props;

    // If the iframe can be embedded in page, then simply activate it
    if (this.allowInPageEmbed()) {
      this.setState({ activatedAt: Date.now() });

      return;
    }

    // Determine if the iframe should be opened in either a lightbox or in
    // a new window
    if (
      isAndroid() ||
      isIOS() ||
      this.props.model.target === IframeTargets.WINDOW
    ) {
      window.open(this.getForwardedUrl(false), "_blank");

      return;
    }

    // ... Otherwise determine if we can open the iframe in a lightbox
    if (openLightbox && this.wrapperRef.current) {
      openLightbox({
        model,
        node: this.wrapperRef.current,
      });
    }
  };

  /**
   * Listener that's going to be triggered when the iframe has been loaded,
   * that specifying if it's ready for display.
   */
  protected onIframeLoad = () => {
    // Wait at least 1200ms from the point where the frame was activated,
    // before marking it to be ready
    const activatedAt = this.state.activatedAt || Date.now();
    const delay = Math.max(0, 1200 - (activatedAt - Date.now()));

    setTimeout(() => {
      this.setState({ isReady: true });
    }, delay);
  };

  /**
   * Helper that calculates the desired aspect ratio of the component (falling
   * back to a hardcoded 16:9 if the component cannot be embedded at it's
   * current position).
   */
  @memoize()
  protected getAspectRatio(): number {
    const { model, inLightbox } = this.props;

    return model.inPageEmbed || inLightbox
      ? model.aspectRatio.height / model.aspectRatio.width
      : 0.5625;
  }

  /**
   * Helper that'll calculate the maximum allowed width based on the current
   * aspect ratio and the defined max size of the iframe.
   */
  protected getMaxWidth(): number | undefined {
    const { model } = this.props;

    // Bail out if neither max width or max height is defined
    if (!model.maxSize) {
      return undefined;
    }

    // Prepare variables to conntain max width, as calculated by width and
    // height based on aspect ratio
    let maxWidthByWidth: number | undefined;
    let maxWidthByHeight: number | undefined;

    if (model.maxSize.width !== undefined) {
      maxWidthByWidth = model.maxSize.width;
    }

    if (model.maxSize.height !== undefined) {
      maxWidthByHeight = model.maxSize.height / this.getAspectRatio();
    }

    // Return the lowest of the two values
    if (maxWidthByWidth !== undefined && maxWidthByHeight !== undefined) {
      return Math.min(maxWidthByWidth, maxWidthByHeight);
    }

    if (maxWidthByWidth !== undefined) {
      return maxWidthByWidth;
    }

    if (maxWidthByHeight !== undefined) {
      return maxWidthByHeight;
    }

    // If we get here, max width cannot be calculated
    return undefined;
  }

  /**
   * Helper that'll return a version of the iframe url, which will be run
   * through the Gateway v3 secure forward if needed.
   */
  protected getForwardedUrl(cache?: boolean): string {
    // Generate the forwarded url
    const forwardedUrl = gGateway.forward(
      this.props.model.src.replace(/\{userId\}/g, this.props.userId || "guest"),
    );

    return cache !== false
      ? (this.forwardedUrl = this.forwardedUrl || forwardedUrl)
      : forwardedUrl;
  }

  /**
   * Helper that'll determine if in page embedding is allowed for the iframe
   * in it's current state (ie. if it meets the minimum size requirements and
   * the appropriate settings are enabled).
   */
  protected allowInPageEmbed(): boolean {
    const { model } = this.props;

    // If in page embedding is disabled, then bail out instantly
    if (!model.inPageEmbed) {
      return false;
    }

    // Bail out if we're unable to locate the wrapper node
    if (!this.wrapperRef.current) {
      return false;
    }

    // Never allow in page embedding on iOS and Android based devices, as
    // that can lead to unexpected behaviour
    if (isAndroid() || isIOS()) {
      return false;
    }

    // ... Otherwise check to see if we have enough available space to
    // render the iframe
    return this.wrapperRef.current.offsetWidth >= model.minSize.width;
  }

  /**
   * Helper that'll preload the preview image, making sure that state is going
   * to be adjusted accordingly once it's available.
   */
  protected async preloadPreviewImage(): Promise<void> {
    const { model } = this.props;
    const startedAt = Date.now();

    // Generate a url to the preview image which we're goinng to use when
    // rendering the iframe in a non-activated embed mode
    const imageUrl = await model.getPreviewImageUrl(this.getAspectRatio());

    if (imageUrl) {
      // Start by preloading the preview image, so we konw that it's going
      // to be available for rendering
      await preloadImage(imageUrl);

      // Wait at least 650 seconds before crossfadig from the preloader to
      // the error message
      await asyncTimeout(Math.max(0, 650 - (Date.now() - startedAt)));

      // Display the preview image!
      this.setState({ previewImageUrl: imageUrl });
    } else {
      // Wait at least 650 seconds before crossfadig from the preloader to
      // the error message
      await asyncTimeout(Math.max(0, 650 - (Date.now() - startedAt)));

      // If we're unable to generate or load a preview image for the
      // iframe, we have no choice but to fall back to a text-based
      // rendering
      this.setState({ previewImageError: true });
    }
  }
}
